import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Star } from "lucide-react";
import { getPerformanceDashboard } from "../../../Services/adminReporting";
import { Card, CardContent } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { cn } from "../../../lib/utils";
import { WindowToggle, FilterChip, fmtMs, fmtSeconds } from "../../../Components/admin/reporting/primitives";
import {
  CARD_CLASS,
  ComparePeriodBar,
  CustomRangePopover,
  DashboardSurface,
  DeltaBadge,
  DemographicsPopover,
  ElementFunnel,
  HistogramChart,
  HOUR_LABELS,
  Panel,
  Toolbar,
  WEEKDAY_LABELS,
  periodRange,
} from "../../../Components/admin/reporting/controls";

const SERIES_COLORS = ["#0ea5e9", "#10b981", "#8b5cf6", "#f59e0b", "#ef4444"];
const TREND_DIMENSIONS = [
  { key: "session_type", label: "Input mode" },
  { key: "entry_point", label: "Entry point" },
];
const BREAKDOWNS = [
  { key: "session_type", label: "Input mode" },
  { key: "user_type", label: "User type" },
  { key: "model_version", label: "Model" },
];
// Filter chip rows shown in the control bar (bare dimension names).
// VERSION (prompt_version) intentionally omitted for now.
const CHIP_DIMS = [
  { key: "user_type", label: "User type" },
  { key: "session_type", label: "Type" },
  { key: "model_version", label: "Model" },
];

const fmtNum = (v) => (v == null ? "—" : Number(v).toLocaleString());

/** Single-value KPI tile. */
const Stat = ({ label, primary, sub, highlight }) => (
  <Card className={cn(CARD_CLASS, highlight && "border-sky-300 bg-sky-50/60")}>
    <CardContent className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className={cn("mt-1 text-2xl font-semibold tabular-nums", highlight && "text-sky-600")}>
        {primary ?? "—"}
      </div>
      {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
    </CardContent>
  </Card>
);

/** A · B · Δ compare tile. */
const CmpStat = ({ label, a, b, fmt = fmtNum, goodWhenDown = false }) => (
  <Card className={CARD_CLASS}>
    <CardContent className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 flex items-baseline gap-3">
        <span className="text-xl font-semibold tabular-nums text-sky-600">{fmt(a)}</span>
        <span className="text-muted-foreground">·</span>
        <span className="text-xl font-semibold tabular-nums text-amber-600">{fmt(b)}</span>
      </div>
      <div className="mt-1.5 flex items-center gap-1.5 border-t pt-1.5">
        <DeltaBadge a={a} b={b} goodWhenDown={goodWhenDown} />
        <span className="text-[10px] uppercase text-muted-foreground">Δ A vs B</span>
      </div>
    </CardContent>
  </Card>
);

const RatingStars = ({ distribution }) => {
  const total = Object.values(distribution || {}).reduce((s, n) => s + n, 0);
  return (
    <div className="space-y-2.5">
      {[5, 4, 3, 2, 1].map((score) => {
        const count = distribution?.[String(score)] || 0;
        const pct = total ? Math.round((count / total) * 100) : 0;
        return (
          <div key={score} className="flex items-center gap-3">
            <div className="flex w-20 shrink-0 items-center gap-0.5">
              {Array.from({ length: score }).map((_, i) => (
                <Star key={i} className="h-3 w-3 fill-amber-500 text-amber-500" />
              ))}
            </div>
            <div className="h-3.5 flex-1 overflow-hidden rounded bg-muted/60">
              <div className="h-full rounded bg-amber-500/80" style={{ width: `${Math.max(pct, count ? 2 : 0)}%` }} />
            </div>
            <div className="w-20 shrink-0 text-right text-xs tabular-nums text-muted-foreground">
              {count} · {pct}%
            </div>
          </div>
        );
      })}
    </div>
  );
};

const AdminPerformance = () => {
  const [windowDays, setWindowDays] = useState(30);
  const [range, setRange] = useState(null); // {start,end} custom | null
  const [filter, setFilter] = useState(null); // {dimension,value} | null (chips)
  const [demoPredicate, setDemoPredicate] = useState(null); // {dim:[vals]} | null
  const [compareMode, setCompareMode] = useState(false);
  const [periodA, setPeriodA] = useState("this_month");
  const [periodB, setPeriodB] = useState("last_month");
  const [trendDim, setTrendDim] = useState("session_type");
  const [sessionsChart, setSessionsChart] = useState("bar");

  const [data, setData] = useState(null); // single-period payload
  const [cmp, setCmp] = useState(null); // {a, b} compare payloads
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (compareMode) {
        const [a, b] = await Promise.all([
          getPerformanceDashboard(windowDays, filter, periodRange(periodA), demoPredicate),
          getPerformanceDashboard(windowDays, filter, periodRange(periodB), demoPredicate),
        ]);
        setCmp({ a, b });
      } else {
        setData(await getPerformanceDashboard(windowDays, filter, range, demoPredicate));
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load performance data.");
    } finally {
      setLoading(false);
    }
  }, [compareMode, windowDays, filter, range, demoPredicate, periodA, periodB]);

  useEffect(() => {
    load();
  }, [load]);

  // Selecting any chip/demographic replaces the single active filter.
  const toggleFilter = (dimension, value) =>
    setFilter((cur) =>
      cur?.dimension === dimension && cur?.value === value
        ? null
        : { dimension, value }
    );

  const chipValues = useMemo(() => {
    const out = {};
    CHIP_DIMS.forEach((d) => {
      out[d.key] = (data?.breakdowns?.[d.key] || [])
        .map((r) => r.value)
        .filter((v) => v && v !== "unknown" && v !== "unversioned" && v !== "Unknown")
        .slice(0, 5);
    });
    return out;
  }, [data]);

  const trendChart = useMemo(() => {
    const series = data?.trend_by_dimension?.[trendDim] || [];
    const byDate = {};
    series.forEach((entry) =>
      entry.points.forEach((p) => {
        byDate[p.date] = byDate[p.date] || { date: p.date };
        byDate[p.date][entry.value] = p.sessions;
      })
    );
    return {
      rows: Object.values(byDate).sort((x, y) => x.date.localeCompare(y.date)),
      values: series.map((e) => e.value),
    };
  }, [data, trendDim]);

  const kpis = data?.kpis || {};
  const A = cmp?.a?.kpis || {};
  const B = cmp?.b?.kpis || {};

  return (
    <DashboardSurface>
     <div className="space-y-7">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Performance</h1>
          <p className="text-sm text-muted-foreground">
            App-usage &amp; quality KPIs.
            {data?.window && !compareMode
              ? ` ${data.window.start} → ${data.window.end}`
              : ""}
          </p>
        </div>
        {!compareMode ? (
          <WindowToggle
            value={range ? null : windowDays}
            onChange={(d) => {
              setRange(null);
              setWindowDays(d);
            }}
          />
        ) : null}
      </div>

      {/* Control bar */}
      <Toolbar>
        {CHIP_DIMS.map((dim) =>
          chipValues[dim.key]?.length ? (
            <div key={dim.key} className="flex items-center gap-1.5">
              <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {dim.label}
              </span>
              {chipValues[dim.key].map((v) => (
                <FilterChip
                  key={v}
                  active={filter?.dimension === dim.key && filter?.value === v}
                  onClick={() => toggleFilter(dim.key, v)}
                >
                  {v}
                </FilterChip>
              ))}
            </div>
          ) : null
        )}
        {!compareMode ? (
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Days
            </span>
            <CustomRangePopover
              active={range}
              onApply={(r) => setRange(r)}
              onClear={() => setRange(null)}
            />
          </div>
        ) : null}
        <DemographicsPopover
          options={data?.demographic_options || cmp?.a?.demographic_options}
          value={demoPredicate}
          onChange={setDemoPredicate}
        />
        <button
          type="button"
          onClick={() => setCompareMode((m) => !m)}
          className={cn(
            "ml-auto rounded-full border px-3 py-1 text-xs font-medium",
            compareMode
              ? "border-amber-500 bg-amber-50 text-amber-700"
              : "border-amber-400/60 text-amber-700 hover:bg-amber-50"
          )}
        >
          ⇄ Compare periods
        </button>
      </Toolbar>

      {compareMode ? (
        <ComparePeriodBar
          a={periodA}
          b={periodB}
          onChange={(a, b) => {
            setPeriodA(a);
            setPeriodB(b);
          }}
          onClose={() => setCompareMode(false)}
        />
      ) : null}

      {filter || demoPredicate ? (
        <p className="text-xs text-muted-foreground">
          {[
            filter ? filter.value : null,
            (data || cmp?.a)?.filter?.value || null,
          ]
            .filter(Boolean)
            .join(" + ")}
          {kpis.rating_is_global ? " · rating shown is global" : ""}
        </p>
      ) : null}

      {error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      {loading && !data && !cmp ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-28 w-full" />
          ))}
        </div>
      ) : compareMode && cmp ? (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <CmpStat label="Sessions" a={A.sessions} b={B.sessions} />
            <CmpStat label="Users" a={A.active_users} b={B.active_users} />
            <CmpStat label="Avg rating" a={A.avg_rating} b={B.avg_rating} fmt={(v) => (v == null ? "—" : v)} />
            <CmpStat label="Med length" a={A.median_duration_sec} b={B.median_duration_sec} fmt={fmtSeconds} />
            <CmpStat label="Med turns" a={A.median_turns} b={B.median_turns} fmt={(v) => (v == null ? "—" : v)} />
            <CmpStat label="Med latency" a={A.median_latency_ms} b={B.median_latency_ms} fmt={fmtMs} goodWhenDown />
          </div>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <CmpStat label="Return rate" a={A.return_rate} b={B.return_rate} fmt={(v) => (v == null ? "—" : `${Math.round(v * 100)}%`)} />
            <CmpStat label="Sessions / user" a={A.sessions_per_user} b={B.sessions_per_user} fmt={(v) => (v == null ? "—" : v)} />
            <CmpStat label="Sessions / day" a={A.sessions_per_day} b={B.sessions_per_day} fmt={(v) => (v == null ? "—" : v)} />
            <CmpStat label="Word ratio" a={A.user_ai_word_ratio} b={B.user_ai_word_ratio} fmt={(v) => (v == null ? "—" : v)} />
          </div>
          <Panel
            title="Element completion"
            right={
              <span className="text-xs text-muted-foreground">
                {periodRange(periodA).label} vs {periodRange(periodB).label}
              </span>
            }
          >
            <ElementFunnel data={{ a: cmp.a.element_completion, b: cmp.b.element_completion }} compare />
          </Panel>
        </>
      ) : data ? (
        <>
          {/* KPI cards */}
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
            <Stat label="Sessions" primary={fmtNum(kpis.sessions)} sub={`${kpis.sessions_per_day ?? "—"} / day`} />
            <Stat label="Users" primary={fmtNum(kpis.active_users)} sub={`${kpis.avg_dau ?? "—"} avg DAU`} />
            <Stat label="Rating" primary={kpis.avg_rating ?? "—"} sub={`${kpis.rating_count ?? 0} rated`} />
            <Stat label="Length" primary={fmtSeconds(kpis.median_duration_sec)} sub={`${kpis.median_turns ?? "—"} turns`} />
            <Stat label="Words" primary={kpis.avg_user_words ?? "—"} sub={`${kpis.avg_coach_words ?? "—"} AI`} />
            <Stat label="Ratio" primary={kpis.user_ai_word_ratio ?? "—"} sub="user : AI" />
          </div>

          {/* Engagement */}
          <div>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Engagement
            </h2>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat label="Return rate" primary={kpis.return_rate != null ? `${Math.round(kpis.return_rate * 100)}%` : "—"} sub="returned after first session" />
              <Stat label="Sessions / user" primary={kpis.sessions_per_user ?? "—"} sub={`across ${data.window?.days}d`} />
              <Stat label="Days between sessions" primary={kpis.avg_days_between_sessions ?? "—"} sub="lower = more frequent" />
              <Stat label="Median latency" primary={fmtMs(kpis.median_latency_ms)} sub={`p90 ${fmtMs(kpis.p90_latency_ms)}`} highlight />
            </div>
          </div>

          {/* Element completion */}
          <Panel title="Element completion" right={<span className="text-xs text-muted-foreground">recommended order · once per user</span>}>
            <ElementFunnel data={data.element_completion} />
          </Panel>

          {/* Trend by dimension */}
          <Panel
            title="Trend — one line per value"
            right={
              <div className="inline-flex rounded-md border bg-muted/40 p-0.5">
                {TREND_DIMENSIONS.map((d) => (
                  <button
                    key={d.key}
                    type="button"
                    onClick={() => setTrendDim(d.key)}
                    className={cn("rounded-[5px] px-3 py-1 text-xs font-medium", trendDim === d.key ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            }
          >
            <div className="h-60">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={trendChart.rows}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                  <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={32} />
                  <Tooltip />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  {trendChart.values.map((v, i) => (
                    <Line key={v} type="monotone" dataKey={v} stroke={SERIES_COLORS[i % SERIES_COLORS.length]} strokeWidth={2} dot={{ r: 2 }} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          </Panel>

          {/* Sessions + ratings */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel
              title="Sessions — daily"
              className="lg:col-span-2"
              right={
                <div className="inline-flex rounded-md border bg-muted/40 p-0.5">
                  {["bar", "line"].map((k) => (
                    <button key={k} type="button" onClick={() => setSessionsChart(k)} className={cn("rounded-[5px] px-3 py-1 text-xs font-medium uppercase", sessionsChart === k ? "bg-background shadow-sm" : "text-muted-foreground hover:text-foreground")}>
                      {k}
                    </button>
                  ))}
                </div>
              }
            >
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  {sessionsChart === "bar" ? (
                    <BarChart data={data.daily_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={32} />
                      <Tooltip />
                      <Bar dataKey="sessions" fill="#818cf8" radius={[3, 3, 0, 0]} />
                    </BarChart>
                  ) : (
                    <LineChart data={data.daily_trend || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                      <XAxis dataKey="date" tick={{ fontSize: 11 }} minTickGap={24} />
                      <YAxis tick={{ fontSize: 11 }} allowDecimals={false} width={32} />
                      <Tooltip />
                      <Line type="monotone" dataKey="sessions" stroke="#818cf8" strokeWidth={2} dot={false} />
                      <Line type="monotone" dataKey="active_users" stroke="#10b981" strokeWidth={2} dot={false} />
                    </LineChart>
                  )}
                </ResponsiveContainer>
              </div>
            </Panel>
            <Panel title="Rating distribution">
              <RatingStars distribution={data.rating_distribution} />
            </Panel>
          </div>

          {/* Active hours + days */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Panel title="Active hours" className="lg:col-span-2" right={<span className="text-xs text-muted-foreground">UTC</span>}>
              <HistogramChart counts={data.active_hours} labels={HOUR_LABELS} />
            </Panel>
            <Panel title="Active days">
              <HistogramChart counts={data.active_days} labels={WEEKDAY_LABELS} color="#b45309" />
            </Panel>
          </div>

          {/* Breakdown tables */}
          {BREAKDOWNS.map((bd) => {
            const rows = data.breakdowns?.[bd.key] || [];
            if (!rows.length) return null;
            return (
              <Panel key={bd.key} title={`By ${bd.label.toLowerCase()}`}>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-2 pr-3 font-medium">{bd.label}</th>
                        <th className="py-2 px-3 text-right font-medium">Sessions</th>
                        <th className="py-2 px-3 text-right font-medium">Avg latency</th>
                        <th className="py-2 px-3 text-right font-medium">Med duration</th>
                        <th className="py-2 px-3 text-right font-medium">Med turns</th>
                        <th className="py-2 px-3 text-right font-medium">User words</th>
                        <th className="py-2 pl-3 text-right font-medium">AI words</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((r) => (
                        <tr key={r.value} className="border-b last:border-0">
                          <td className="py-2 pr-3 font-medium">{r.value}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{r.sessions}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmtMs(r.avg_latency_ms)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{fmtSeconds(r.median_duration_sec)}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{r.median_turns ?? "—"}</td>
                          <td className="py-2 px-3 text-right tabular-nums">{r.avg_user_words ?? "—"}</td>
                          <td className="py-2 pl-3 text-right tabular-nums">{r.avg_coach_words ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Panel>
            );
          })}

          <p className="text-xs text-muted-foreground">
            Latency = user message received → response ready. Active hours/days
            and user-type populate after the next reporting rollup.
          </p>
        </>
      ) : null}
     </div>
    </DashboardSurface>
  );
};

export default AdminPerformance;
